import axios from 'axios';
import { isInLoginGracePeriod } from './api';

const animalAPI = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token
animalAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para lidar com erros
animalAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Não invalidar token se estivermos no período de graça após login
      if (isInLoginGracePeriod()) {
        console.warn('[AnimalAPI] 401 durante período de graça após login, ignorando invalidação de token');
        return Promise.reject(error);
      }
      
      const isLoginPage = window.location.pathname === '/login';
      if (!isLoginPage) {
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
          localStorage.removeItem('token');
          localStorage.removeItem('default_company_id');
          window.dispatchEvent(new CustomEvent('auth:token-invalid'));
        }
      }
    }
    return Promise.reject(error);
  }
);

export type AnimalType = 'dog' | 'cat' | 'bird' | 'other';
export type AnimalBehavior = 'friendly' | 'shy' | 'aggressive' | 'unknown';

export interface Animal {
  id: string;
  property_id: string;
  name: string;
  type: AnimalType;
  breed?: string;
  behavior: AnimalBehavior;
  size?: string;
  notes?: string;
  is_dangerous: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateAnimalRequest {
  property_id: string;
  name: string;
  type: AnimalType;
  breed?: string;
  behavior?: AnimalBehavior;
  size?: string;
  notes?: string;
  is_dangerous?: boolean;
}

export interface UpdateAnimalRequest {
  name?: string;
  type?: AnimalType;
  breed?: string;
  behavior?: AnimalBehavior;
  size?: string;
  notes?: string;
  is_dangerous?: boolean;
}

/**
 * Busca todos os animais de uma propriedade
 */
export const getAnimalsByProperty = async (propertyId: string): Promise<Animal[]> => {
  try {
    const response = await animalAPI.get('/animals', {
      params: { property_id: propertyId }
    });
    return response.data.animals || [];
  } catch (error: any) {
    console.error('Erro ao buscar animais:', error);
    throw error;
  }
};

/**
 * Busca um animal pelo ID
 */
export const getAnimalById = async (animalId: string): Promise<Animal> => {
  try {
    const response = await animalAPI.get(`/animals/${animalId}`);
    return response.data;
  } catch (error: any) {
    console.error('Erro ao buscar animal:', error);
    throw error;
  }
};

/**
 * Cria um novo animal
 */
export const createAnimal = async (data: CreateAnimalRequest): Promise<Animal> => {
  try {
    const response = await animalAPI.post('/animals', data);
    return response.data;
  } catch (error: any) {
    console.error('Erro ao criar animal:', error);
    throw error;
  }
};

/**
 * Atualiza um animal
 */
export const updateAnimal = async (animalId: string, data: UpdateAnimalRequest): Promise<Animal> => {
  try {
    const response = await animalAPI.put(`/animals/${animalId}`, data);
    return response.data;
  } catch (error: any) {
    console.error('Erro ao atualizar animal:', error);
    throw error;
  }
};

/**
 * Deleta um animal
 */
export const deleteAnimal = async (animalId: string): Promise<void> => {
  try {
    await animalAPI.delete(`/animals/${animalId}`);
  } catch (error: any) {
    console.error('Erro ao deletar animal:', error);
    throw error;
  }
};

/**
 * Busca animais perigosos (com alertas de segurança)
 */
export const getDangerousAnimals = async (propertyId?: string): Promise<Animal[]> => {
  try {
    const params = propertyId ? { property_id: propertyId } : {};
    const response = await animalAPI.get('/animals/dangerous', { params });
    return response.data.animals || [];
  } catch (error: any) {
    console.error('Erro ao buscar animais perigosos:', error);
    throw error;
  }
};

/**
 * Busca animais por comportamento
 */
export const getAnimalsByBehavior = async (behavior: AnimalBehavior): Promise<Animal[]> => {
  try {
    const response = await animalAPI.get('/animals/behavior', {
      params: { behavior }
    });
    return response.data.animals || [];
  } catch (error: any) {
    console.error('Erro ao buscar animais por comportamento:', error);
    throw error;
  }
};

export default {
  getAnimalsByProperty,
  getAnimalById,
  createAnimal,
  updateAnimal,
  deleteAnimal,
  getDangerousAnimals,
  getAnimalsByBehavior,
};
